/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { RefObject, useMemo, useRef, useState } from "react";
import { theme } from "styles/theme";
import Button from "uis/Button";
import InputField from "uis/InputField";
import DaumPostcode from "react-daum-postcode";
import Modal from "react-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "quill-image-uploader/dist/quill.imageUploader.min.css";
import { uploadImage2 } from "apis/uploadImage";
import Compressor from "compressorjs";
import {
    Annotation,
    DropdownContainer,
    DropdownIcon,
    DropdownLabel,
    MessageContainer,
    SubContainer,
    TabBtn,
    TabContainer,
    Title,
} from "./uis";
import Icon from "uis/Icon";
import { TKakaoAlimContent } from "apis/visitors/setting/kakao-alimtalk";

const SectionContainer = styled.div<{ open?: boolean }>`
    flex-direction: column;
    gap: 14px;
    display: ${(props) => (props.open ? "flex" : "none")};
    padding: 40px 24px;
    border-bottom: 1px solid ${theme.neutral50};
`;

const modalStyle = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        width: "1000px",

        borderRadius: "20px",
        transform: "translate(-50%, -50%)",
    },
    overlay: {
        backgroundColor: "var(--component-color-material-dimmer)",
        zIndex: 1000,
    },
};
Modal.setAppElement("#root");

export const EditVisitGuide = ({ data, setData }: { data: TKakaoAlimContent; setData: (data: TKakaoAlimContent) => void }) => {
    const [isOpenAddressModal, setIsOpenAddressModal] = useState<boolean>(false);
    const additionalQuillKoreanRef = useRef<ReactQuill>(null);
    const additionalQuillEnglishRef = useRef<ReactQuill>(null);
    const directionalQuillKoreanRef = useRef<ReactQuill>(null);
    const directionalQuillEnglishRef = useRef<ReactQuill>(null);

    const [isOpenAddress, setIsOpenAddress] = useState<boolean>(true);
    const [isOpenMessage, setIsOpenMessage] = useState<boolean>(true);
    const [page, setPage] = useState<string>("한국어");

    const useImageUploadQuill = (quillRef: RefObject<ReactQuill>) => {
        return useMemo(() => {
            return {
                toolbar: {
                    container: [
                        [{ header: [1, 2, false] }],
                        ["bold", "italic", "underline", "strike", "blockquote"],
                        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                        ["link", "image"],
                    ],
                    handlers: {
                        image: () => {
                            const input = document.createElement("input");

                            input.setAttribute("type", "file");
                            input.setAttribute("accept", "image/*");
                            input.click();

                            input.onchange = () => {
                                const file = input.files?.[0];
                                if (file)
                                    new Compressor(file, {
                                        quality: 0.3,
                                        success: async (compressedResult) => {
                                            const formData = new FormData();
                                            formData.append("file", compressedResult);
                                            const res = await uploadImage2(formData);
                                            if (res) {
                                                const range = quillRef.current?.getEditor().getSelection(true);
                                                quillRef.current?.getEditor().insertEmbed(range ? range.index : 0, "image", res);
                                                quillRef.current?.getEditor().setSelection(range ? range.index + 1 : 1, 1);
                                            }
                                        },
                                    });
                            };
                        },
                    },
                },
            };
        }, [quillRef]);
    };

    const additional_korean_modules = useImageUploadQuill(additionalQuillKoreanRef);
    const directional_korean_modules = useImageUploadQuill(directionalQuillKoreanRef);

    const additional_english_modules = useImageUploadQuill(additionalQuillEnglishRef);
    const directional_english_modules = useImageUploadQuill(directionalQuillEnglishRef);

    return (
        <SubContainer>
            <Annotation>
                * 방문 일정 예약시, 방문자에게 카카오톡 알림톡으로 전송되는 방문 일정 안내 페이지에 표시되는 정보입니다.
            </Annotation>
            <DropdownContainer onClick={() => setIsOpenAddress(!isOpenAddress)}>
                <DropdownLabel>주소 설정</DropdownLabel>
                <DropdownIcon open={isOpenAddress}>
                    <Icon name="top-arrow" />
                </DropdownIcon>
            </DropdownContainer>
            <SectionContainer open={isOpenAddress}>
                <div
                    css={css`
                        display: flex;
                        gap: 12px;
                    `}
                >
                    <InputField
                        type="disabled"
                        placeholder={data.addressForMap ? data.addressForMap : "주소"}
                        styles={{
                            container: css`
                                width: 400px;
                            `,
                        }}
                    />
                    <Button
                        onClick={() => {
                            setIsOpenAddressModal(!isOpenAddressModal);
                        }}
                        title="주소찾기"
                        type="secondary"
                        styles={{
                            container: css`
                                width: 100px;
                            `,
                        }}
                    />
                </div>
                <InputField
                    defaultValue={data.address.split(data.addressForMap)[1]}
                    onChange={(e) => {
                        setData({ ...data, address: `${data.addressForMap} ${e}` });
                    }}
                    placeholder={"상세 주소 입력"}
                    styles={{
                        container: css`
                            width: 400px;
                        `,
                        input: css`
                            color: ${theme.neutral500};
                        `,
                    }}
                />
            </SectionContainer>
            <DropdownContainer onClick={() => setIsOpenMessage(!isOpenMessage)}>
                <DropdownLabel>안내 메세지</DropdownLabel>
                <DropdownIcon open={isOpenMessage}>
                    <Icon name="top-arrow" />
                </DropdownIcon>
            </DropdownContainer>
            {isOpenMessage && (
                <>
                    <TabContainer>
                        {["한국어", "영어"].map((item, index) => (
                            <TabBtn key={index} onClick={() => setPage(item)} isPressed={item === page}>
                                {item}
                            </TabBtn>
                        ))}
                    </TabContainer>
                    <MessageContainer>
                        <Title>추가 안내사항</Title>
                        {page === "한국어" && (
                            <ReactQuill
                                ref={additionalQuillKoreanRef}
                                modules={additional_korean_modules}
                                value={data.additionalInfosHtmlInKoreanFormat}
                                onChange={(e) => {
                                    setData({ ...data, additionalInfosHtmlInKoreanFormat: e });
                                }}
                                css={css`
                                    width: 500px;
                                `}
                            />
                        )}
                        {page === "영어" && (
                            <ReactQuill
                                ref={additionalQuillEnglishRef}
                                modules={additional_english_modules}
                                value={data.additionalInfosHtmlInEnglishFormat}
                                onChange={(e) => {
                                    setData({ ...data, additionalInfosHtmlInEnglishFormat: e });
                                }}
                                css={css`
                                    width: 500px;
                                `}
                            />
                        )}
                    </MessageContainer>
                    <MessageContainer>
                        <Title>오시는길 상세 설명</Title>
                        {page === "한국어" && (
                            <ReactQuill
                                ref={directionalQuillKoreanRef}
                                modules={directional_korean_modules}
                                value={data.directionsHtmlInKoreanFormat}
                                onChange={(e) => {
                                    setData({ ...data, directionsHtmlInKoreanFormat: e });
                                }}
                                css={css`
                                    width: 500px;
                                `}
                            />
                        )}
                        {page === "영어" && (
                            <ReactQuill
                                ref={directionalQuillEnglishRef}
                                modules={directional_english_modules}
                                value={data.directionsHtmlInEnglishFormat}
                                onChange={(e) => {
                                    setData({ ...data, directionsHtmlInEnglishFormat: e });
                                }}
                                css={css`
                                    width: 500px;
                                `}
                            />
                        )}
                    </MessageContainer>
                </>
            )}

            {isOpenAddressModal && (
                <Modal isOpen={isOpenAddressModal} onRequestClose={() => setIsOpenAddressModal(false)} style={modalStyle}>
                    <DaumPostcode
                        onComplete={(e) => {
                            setIsOpenAddressModal(false);
                            setData({ ...data, addressForMap: e.address });
                        }}
                    />
                </Modal>
            )}
        </SubContainer>
    );
};
